import { z } from 'zod';

/** Срок сдачи отчёта смены по участку (ПР-05). Час 0–23 по часовому поясу компании; по умолчанию 20:00. */
export const DEFAULT_SITE_REPORT_DEADLINE_HOUR = 20;

export const SiteReportDeadlineSettings = z.strictObject({
  hour: z.number().int().min(0).max(23),
});
export type SiteReportDeadlineSettings = z.infer<typeof SiteReportDeadlineSettings>;

export const SiteReportDeadlineSettingsInput = SiteReportDeadlineSettings;
export type SiteReportDeadlineSettingsInput = z.infer<typeof SiteReportDeadlineSettingsInput>;

export const SiteMissingReportsQuery = z.strictObject({
  date: z.iso.date().optional(),
});
export type SiteMissingReportsQuery = z.infer<typeof SiteMissingReportsQuery>;

export interface SiteMissingReportItem {
  projectId: string;
  projectCode: string;
  projectName: string;
  packageId: string;
  packageName: string;
  foremanId: string | null;
  foremanName: string | null;
}

export interface SiteMissingReports {
  date: string;
  hour: number;
  timezone: string;
  /** Уже после часа сдачи в часовом поясе компании за эту дату */
  pastDeadline: boolean;
  items: SiteMissingReportItem[];
}

export interface SiteReportDeadlineView {
  /** Настроенный час */
  hour: number;
  /** Час, действующий сегодня: новый час после срока дня действует с завтрашнего дня */
  todayHour: number;
  /** С какого дня действует настроенный час, если позже сегодняшнего */
  hourFrom: string | null;
  timezone: string;
  /** «Сегодня» компании в этом поясе */
  today: string;
  pastDeadline: boolean;
}

/** Цель ПМ-05: отчёт смены сдан до срока в 90 % участко-дней */
export const SITE_ON_TIME_TARGET = 0.9;
/** Наибольший период отчёта о сдаче, дней */
export const SITE_ON_TIME_MAX_DAYS = 92;

export const SiteOnTimeQuery = z.strictObject({
  from: z.iso.date(),
  to: z.iso.date(),
  projectId: z.uuid().optional(),
});
export type SiteOnTimeQuery = z.infer<typeof SiteOnTimeQuery>;

/** Участко-дни: рабочий день по календарю графика, на который выдан пакет работ */
export interface SiteOnTimeCounts {
  /** Участко-дней, по которым отчёт должен был быть сдан */
  total: number;
  /** Сдан на проверку до срока того дня */
  onTime: number;
  /** Сдан, но после срока */
  late: number;
  /** Не сдан */
  missing: number;
  /** onTime / total; null, если участко-дней нет */
  share: number | null;
}

export interface SiteOnTimeProject extends SiteOnTimeCounts {
  projectId: string;
  projectCode: string;
  projectName: string;
}

export interface SiteOnTimeDay extends SiteOnTimeCounts {
  date: string;
  /** Час сдачи, действовавший в этот день */
  hour: number;
}

export interface SiteOnTimeReport extends SiteOnTimeCounts {
  from: string;
  to: string;
  timezone: string;
  target: number;
  /** Сегодняшний день считается только после часа сдачи; будущие дни не считаются */
  countedTo: string | null;
  projects: SiteOnTimeProject[];
  days: SiteOnTimeDay[];
}

/** Текст сводки дня от Pilot (ПР-12) или запасной шаблон, если Pilot недоступен */
export interface SiteDaySummaryText {
  text: string;
  textSource: 'template' | 'pilot';
  /** Почему остался шаблон: Pilot не подключён, отказал или не ответил */
  pilotNote: string | null;
}
